import { useState, useEffect, useMemo } from 'react';
import { Clock, Coffee, UtensilsCrossed, LogOut, Calendar, TrendingUp, ChevronLeft, ChevronRight, Phone, MessageCircle, Mail } from 'lucide-react';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Table, { TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/Table';
import {
  useCurrentStatus,
  useClockIn,
  useClockOut,
  useStartBreak,
  useEndBreak,
  useStartLunch,
  useEndLunch,
  usePunchesByAgent,
  usePunchesByOrganization,
} from '../../hooks/useTimePunches';
import { useShifts } from '../../hooks/useSchedules';
import { PunchType } from '../../services/time-punches.service';
import { Shift } from '../../services/schedules.service';
import { useAuthStore } from '../../stores/auth.store';
import { useScheduleIntervals } from '../../hooks/useScheduleIntervals';

export default function AgentTimePunchesPage() {
  const { data: currentStatus, isLoading, error } = useCurrentStatus();
  const clockIn = useClockIn();
  const clockOut = useClockOut();
  const startBreak = useStartBreak();
  const endBreak = useEndBreak();
  const startLunch = useStartLunch();
  const endLunch = useEndLunch();

  // Debug logging
  console.log('AgentTimePunchesPage - currentStatus:', currentStatus);
  console.log('AgentTimePunchesPage - isLoading:', isLoading);
  console.log('AgentTimePunchesPage - error:', error);

  const [currentTime, setCurrentTime] = useState(new Date());
  const [workDuration, setWorkDuration] = useState('00:00:00');

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());

      // Calculate work duration if clocked in
      if (currentStatus?.isClocked && currentStatus.currentPunch) {
        const start = new Date(currentStatus.currentPunch.punchTime);
        const diff = Date.now() - start.getTime();
        const hours = Math.floor(diff / 3600000);
        const minutes = Math.floor((diff % 3600000) / 60000);
        const seconds = Math.floor((diff % 60000) / 1000);
        setWorkDuration(
          `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
        );
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [currentStatus]);

  const handleClockIn = async () => {
    try {
      console.log('Clock in button clicked');
      // Try to get geolocation
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            console.log('Got geolocation:', position.coords);
            try {
              const result = await clockIn.mutateAsync({
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
              });
              console.log('Clock in successful:', result);
            } catch (err) {
              console.error('Clock in API error:', err);
              alert('Failed to clock in: ' + (err as any)?.response?.data?.message || (err as Error).message);
            }
          },
          async (_error) => {
            // Fallback if geolocation denied
            console.log('Geolocation denied, clocking in without location');
            try {
              const result = await clockIn.mutateAsync({});
              console.log('Clock in successful:', result);
            } catch (err) {
              console.error('Clock in API error:', err);
              alert('Failed to clock in: ' + (err as any)?.response?.data?.message || (err as Error).message);
            }
          }
        );
      } else {
        console.log('Geolocation not available');
        try {
          const result = await clockIn.mutateAsync({});
          console.log('Clock in successful:', result);
        } catch (err) {
          console.error('Clock in API error:', err);
          alert('Failed to clock in: ' + (err as any)?.response?.data?.message || (err as Error).message);
        }
      }
    } catch (error) {
      console.error('Unexpected error in handleClockIn:', error);
      alert('Unexpected error: ' + (error as Error).message);
    }
  };

  const handleClockOut = async () => {
    if (confirm('Are you sure you want to clock out?')) {
      try {
        const result = await clockOut.mutateAsync({});
        console.log('Clock out successful:', result);
      } catch (err) {
        console.error('Clock out error:', err);
        alert('Failed to clock out: ' + (err as any)?.response?.data?.message || (err as Error).message);
      }
    }
  };

  const handleStartBreak = async () => {
    try {
      const result = await startBreak.mutateAsync();
      console.log('Start break successful:', result);
    } catch (err) {
      console.error('Start break error:', err);
      alert('Failed to start break: ' + (err as any)?.response?.data?.message || (err as Error).message);
    }
  };

  const handleEndBreak = async () => {
    try {
      const result = await endBreak.mutateAsync();
      console.log('End break successful:', result);
    } catch (err) {
      console.error('End break error:', err);
      alert('Failed to end break: ' + (err as any)?.response?.data?.message || (err as Error).message);
    }
  };

  const handleStartLunch = async () => {
    try {
      const result = await startLunch.mutateAsync();
      console.log('Start lunch successful:', result);
    } catch (err) {
      console.error('Start lunch error:', err);
      alert('Failed to start lunch: ' + (err as any)?.response?.data?.message || (err as Error).message);
    }
  };

  const handleEndLunch = async () => {
    try {
      const result = await endLunch.mutateAsync();
      console.log('End lunch successful:', result);
    } catch (err) {
      console.error('End lunch error:', err);
      alert('Failed to end lunch: ' + (err as any)?.response?.data?.message || (err as Error).message);
    }
  };

  const getStatusBadge = () => {
    if (!currentStatus?.isClocked) {
      return <Badge variant="default">Clocked Out</Badge>;
    }

    const lastPunchType = currentStatus.currentPunch?.punchType;
    switch (lastPunchType) {
      case PunchType.CLOCK_IN:
      case PunchType.BREAK_END:
      case PunchType.LUNCH_END:
        return <Badge variant="success">Working</Badge>;
      case PunchType.BREAK_START:
        return <Badge variant="warning">On Break</Badge>;
      case PunchType.LUNCH_START:
        return <Badge variant="warning">On Lunch</Badge>;
      default:
        return <Badge variant="default">Unknown</Badge>;
    }
  };

  const isOnBreak = currentStatus?.currentPunch?.punchType === PunchType.BREAK_START;
  const isOnLunch = currentStatus?.currentPunch?.punchType === PunchType.LUNCH_START;
  const isWorking = currentStatus?.isClocked && !isOnBreak && !isOnLunch;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  // Show error message if user is not linked to an agent
  if (error && (error as any)?.response?.status === 400) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="max-w-md">
          <div className="text-center space-y-4">
            <div className="text-red-600 text-5xl">⚠️</div>
            <h2 className="text-xl font-semibold text-gray-900">Account Not Linked</h2>
            <p className="text-gray-600">
              Your user account is not associated with an agent profile. Please contact your administrator to link your account.
            </p>
            <p className="text-sm text-gray-500">
              Error: {(error as any)?.response?.data?.message || error.message}
            </p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">My Time Clock</h1>
        <p className="text-gray-600 mt-1">Track your work hours and breaks</p>
      </div>

      {/* Main Clock Card */}
      <Card>
        <div className="text-center space-y-6">
          {/* Current Time */}
          <div>
            <p className="text-sm text-gray-600 mb-2">Current Time</p>
            <p className="text-5xl font-bold text-gray-900 tabular-nums">
              {currentTime.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
              })}
            </p>
            <p className="text-lg text-gray-600 mt-2">
              {currentTime.toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              })}
            </p>
          </div>

          {/* Status Badge */}
          <div className="flex justify-center">{getStatusBadge()}</div>

          {/* Work Duration */}
          {currentStatus?.isClocked && (
            <div className="bg-blue-50 rounded-lg p-6">
              <p className="text-sm text-blue-600 mb-2">Current Session</p>
              <p className="text-4xl font-bold text-blue-900 tabular-nums">{workDuration}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="grid grid-cols-2 gap-4 pt-4">
            {!currentStatus?.isClocked ? (
              <Button
                onClick={handleClockIn}
                className="col-span-2 py-6 text-lg"
                disabled={clockIn.isPending}
              >
                <Clock className="w-6 h-6 mr-3" />
                Clock In
              </Button>
            ) : (
              <>
                {!isOnBreak && !isOnLunch && (
                  <>
                    <Button
                      variant="secondary"
                      onClick={handleStartBreak}
                      disabled={startBreak.isPending}
                      className="py-4"
                    >
                      <Coffee className="w-5 h-5 mr-2" />
                      Start Break
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={handleStartLunch}
                      disabled={startLunch.isPending}
                      className="py-4"
                    >
                      <UtensilsCrossed className="w-5 h-5 mr-2" />
                      Start Lunch
                    </Button>
                  </>
                )}

                {isOnBreak && (
                  <Button
                    onClick={handleEndBreak}
                    disabled={endBreak.isPending}
                    className="col-span-2 py-4"
                  >
                    <Coffee className="w-5 h-5 mr-2" />
                    End Break
                  </Button>
                )}

                {isOnLunch && (
                  <Button
                    onClick={handleEndLunch}
                    disabled={endLunch.isPending}
                    className="col-span-2 py-4"
                  >
                    <UtensilsCrossed className="w-5 h-5 mr-2" />
                    End Lunch
                  </Button>
                )}

                {isWorking && (
                  <Button
                    variant="secondary"
                    onClick={handleClockOut}
                    disabled={clockOut.isPending}
                    className="col-span-2 py-4"
                  >
                    <LogOut className="w-5 h-5 mr-2" />
                    Clock Out
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
        <Card>
          <div className="flex items-center">
            <Clock className="h-10 w-10 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Today's Hours</p>
              <p className="text-2xl font-semibold text-gray-900">
                {currentStatus?.todayHours?.toFixed(2) || '0.00'}h
              </p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center">
            <TrendingUp className="h-10 w-10 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">This Week</p>
              <p className="text-2xl font-semibold text-gray-900">
                {currentStatus?.weekHours?.toFixed(2) || '0.00'}h
              </p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center">
            <Calendar className="h-10 w-10 text-purple-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Status</p>
              <p className="text-xl font-semibold text-gray-900">
                {currentStatus?.isClocked ? (
                  currentStatus.currentPunch?.punchType === PunchType.BREAK_START ? 'On Break' :
                  currentStatus.currentPunch?.punchType === PunchType.LUNCH_START ? 'On Lunch' :
                  'Clocked In'
                ) : 'Clocked Out'}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Today's Schedule */}
      <TodayScheduleView />

      {/* Agent Schedule */}
      <AgentScheduleView />

      {/* Recent Punches */}
      <RecentPunches />
    </div>
  );
}

function TodayScheduleView() {
  const user = useAuthStore((state) => state.user);
  const today = useMemo(() => new Date(), []);

  // Fetch today's schedule intervals for the agent
  const { data: fetchedIntervals = [], isLoading } = useScheduleIntervals({
    agentId: user?.agentId,
    date: today,
  });

  // Generate demo data if no intervals exist and user is agent@lentil-labs.com
  const intervals = useMemo(() => {
    if (fetchedIntervals.length > 0) {
      return fetchedIntervals;
    }

    // Only generate demo data for agent@lentil-labs.com
    if (user?.email !== 'agent@lentil-labs.com') {
      return [];
    }

    const todayStr = today.toISOString().split('T')[0];
    const isToday = todayStr === '2026-01-12' || todayStr === '2026-01-13';

    if (!isToday) {
      return [];
    }

    console.log('Generating demo schedule for agent@lentil-labs.com on', todayStr);

    // Generate 8-hour shift: 9 AM to 5 PM with varied activities
    const demoIntervals: any[] = [];
    let intervalIdCounter = 0;

    // Morning Phone Session: 9:00 AM - 10:30 AM (6 intervals)
    demoIntervals.push({
      id: `demo-${todayStr}-${intervalIdCounter++}`,
      scheduleId: 'demo-schedule',
      agentId: user.agentId || 'demo-agent',
      date: todayStr,
      intervalStart: 36, // 9:00 AM
      intervalEnd: 42,   // 10:30 AM
      activityType: 'PHONE',
      channel: 'PHONE',
      isAIGenerated: false,
      isOptimized: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      agent: {
        id: user.agentId || 'demo-agent',
        firstName: user.firstName || 'Agent',
        lastName: user.lastName || 'User',
        email: user.email || 'agent@lentil-labs.com',
      },
    });

    // Morning Break: 10:30 AM - 10:45 AM (1 interval)
    demoIntervals.push({
      id: `demo-${todayStr}-${intervalIdCounter++}`,
      scheduleId: 'demo-schedule',
      agentId: user.agentId || 'demo-agent',
      date: todayStr,
      intervalStart: 42, // 10:30 AM
      intervalEnd: 43,   // 10:45 AM
      activityType: 'BREAK',
      channel: null,
      isAIGenerated: false,
      isOptimized: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      agent: {
        id: user.agentId || 'demo-agent',
        firstName: user.firstName || 'Agent',
        lastName: user.lastName || 'User',
        email: user.email || 'agent@lentil-labs.com',
      },
    });

    // Chat Session: 10:45 AM - 12:00 PM (5 intervals)
    demoIntervals.push({
      id: `demo-${todayStr}-${intervalIdCounter++}`,
      scheduleId: 'demo-schedule',
      agentId: user.agentId || 'demo-agent',
      date: todayStr,
      intervalStart: 43, // 10:45 AM
      intervalEnd: 48,   // 12:00 PM
      activityType: 'CHAT',
      channel: 'CHAT',
      isAIGenerated: false,
      isOptimized: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      agent: {
        id: user.agentId || 'demo-agent',
        firstName: user.firstName || 'Agent',
        lastName: user.lastName || 'User',
        email: user.email || 'agent@lentil-labs.com',
      },
    });

    // Lunch: 12:00 PM - 1:00 PM (4 intervals)
    demoIntervals.push({
      id: `demo-${todayStr}-${intervalIdCounter++}`,
      scheduleId: 'demo-schedule',
      agentId: user.agentId || 'demo-agent',
      date: todayStr,
      intervalStart: 48, // 12:00 PM
      intervalEnd: 52,   // 1:00 PM
      activityType: 'MEAL',
      channel: null,
      isAIGenerated: false,
      isOptimized: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      agent: {
        id: user.agentId || 'demo-agent',
        firstName: user.firstName || 'Agent',
        lastName: user.lastName || 'User',
        email: user.email || 'agent@lentil-labs.com',
      },
    });

    // Afternoon Email Session: 1:00 PM - 2:30 PM (6 intervals)
    demoIntervals.push({
      id: `demo-${todayStr}-${intervalIdCounter++}`,
      scheduleId: 'demo-schedule',
      agentId: user.agentId || 'demo-agent',
      date: todayStr,
      intervalStart: 52, // 1:00 PM
      intervalEnd: 58,   // 2:30 PM
      activityType: 'EMAIL',
      channel: 'EMAIL',
      isAIGenerated: false,
      isOptimized: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      agent: {
        id: user.agentId || 'demo-agent',
        firstName: user.firstName || 'Agent',
        lastName: user.lastName || 'User',
        email: user.email || 'agent@lentil-labs.com',
      },
    });

    // Afternoon Break: 2:30 PM - 2:45 PM (1 interval)
    demoIntervals.push({
      id: `demo-${todayStr}-${intervalIdCounter++}`,
      scheduleId: 'demo-schedule',
      agentId: user.agentId || 'demo-agent',
      date: todayStr,
      intervalStart: 58, // 2:30 PM
      intervalEnd: 59,   // 2:45 PM
      activityType: 'BREAK',
      channel: null,
      isAIGenerated: false,
      isOptimized: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      agent: {
        id: user.agentId || 'demo-agent',
        firstName: user.firstName || 'Agent',
        lastName: user.lastName || 'User',
        email: user.email || 'agent@lentil-labs.com',
      },
    });

    // Final Phone Session: 2:45 PM - 5:00 PM (9 intervals)
    demoIntervals.push({
      id: `demo-${todayStr}-${intervalIdCounter++}`,
      scheduleId: 'demo-schedule',
      agentId: user.agentId || 'demo-agent',
      date: todayStr,
      intervalStart: 59, // 2:45 PM
      intervalEnd: 68,   // 5:00 PM
      activityType: 'PHONE',
      channel: 'PHONE',
      isAIGenerated: false,
      isOptimized: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      agent: {
        id: user.agentId || 'demo-agent',
        firstName: user.firstName || 'Agent',
        lastName: user.lastName || 'User',
        email: user.email || 'agent@lentil-labs.com',
      },
    });

    return demoIntervals;
  }, [fetchedIntervals, user, today]);

  // Helper function to convert interval number (0-95) to time string
  const intervalToTime = (interval: number): string => {
    const hours = Math.floor(interval / 4);
    const minutes = (interval % 4) * 15;
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
    return `${displayHours}:${String(minutes).padStart(2, '0')} ${period}`;
  };

  // Merge consecutive intervals of the same type into blocks
  const scheduleBlocks = useMemo(() => {
    if (intervals.length === 0) return [];

    // Sort by interval start
    const sorted = [...intervals].sort((a, b) => a.intervalStart - b.intervalStart);
    const merged: Array<{
      activityType: string;
      channel?: string | null;
      startTime: string;
      endTime: string;
      intervalStart: number;
      intervalEnd: number;
    }> = [];

    let current = {
      activityType: sorted[0].activityType,
      channel: sorted[0].channel,
      intervalStart: sorted[0].intervalStart,
      intervalEnd: sorted[0].intervalEnd,
      startTime: intervalToTime(sorted[0].intervalStart),
      endTime: intervalToTime(sorted[0].intervalEnd),
    };

    for (let i = 1; i < sorted.length; i++) {
      const interval = sorted[i];

      // If this interval is consecutive and has the same activity type and channel, merge it
      if (
        interval.intervalStart === current.intervalEnd &&
        interval.activityType === current.activityType &&
        interval.channel === current.channel
      ) {
        current.intervalEnd = interval.intervalEnd;
        current.endTime = intervalToTime(interval.intervalEnd);
      } else {
        // Push the current merged block and start a new one
        merged.push({ ...current });
        current = {
          activityType: interval.activityType,
          channel: interval.channel,
          intervalStart: interval.intervalStart,
          intervalEnd: interval.intervalEnd,
          startTime: intervalToTime(interval.intervalStart),
          endTime: intervalToTime(interval.intervalEnd),
        };
      }
    }

    // Push the last block
    merged.push(current);

    return merged;
  }, [intervals]);

  const getActivityIcon = (activityType: string, channel?: string | null) => {
    if (channel === 'PHONE') return <Phone className="h-4 w-4" />;
    if (channel === 'CHAT') return <MessageCircle className="h-4 w-4" />;
    if (channel === 'EMAIL') return <Mail className="h-4 w-4" />;
    if (activityType === 'BREAK') return <Coffee className="h-4 w-4" />;
    if (activityType === 'MEAL') return <UtensilsCrossed className="h-4 w-4" />;
    return <Calendar className="h-4 w-4" />;
  };

  const getActivityBadge = (activityType: string, channel?: string | null) => {
    if (activityType === 'PHONE' || channel === 'PHONE') {
      return <Badge variant="info">Phone</Badge>;
    }
    if (activityType === 'CHAT' || channel === 'CHAT') {
      return <Badge variant="success">Chat</Badge>;
    }
    if (activityType === 'EMAIL' || channel === 'EMAIL') {
      return <Badge variant="default">Email</Badge>;
    }
    if (activityType === 'BREAK') {
      return <Badge variant="warning">Break</Badge>;
    }
    if (activityType === 'MEAL') {
      return <Badge variant="warning">Lunch</Badge>;
    }
    return <Badge variant="default">{activityType}</Badge>;
  };

  const getCurrentBlock = () => {
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentInterval = currentHour * 4 + Math.floor(currentMinute / 15);

    return scheduleBlocks.find(
      (block) => currentInterval >= block.intervalStart && currentInterval < block.intervalEnd
    );
  };

  const currentBlock = getCurrentBlock();

  if (isLoading) {
    return (
      <Card>
        <h3 className="text-lg font-semibold mb-4">Today's Schedule</h3>
        <div className="text-center py-8 text-gray-500">Loading schedule...</div>
      </Card>
    );
  }

  if (scheduleBlocks.length === 0) {
    return (
      <Card>
        <h3 className="text-lg font-semibold mb-4">Today's Schedule</h3>
        <div className="text-center py-8 text-gray-500">No schedule for today</div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="mb-4">
        <h3 className="text-lg font-semibold">Today's Schedule</h3>
        <p className="text-sm text-gray-600">
          {today.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
        </p>
      </div>

      {currentBlock && (
        <div className="mb-4 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="text-blue-600">
                {getActivityIcon(currentBlock.activityType, currentBlock.channel)}
              </div>
              <div>
                <p className="text-sm font-medium text-blue-900">Currently</p>
                <div className="flex items-center gap-2">
                  {getActivityBadge(currentBlock.activityType, currentBlock.channel)}
                  <span className="text-sm text-blue-700">
                    {currentBlock.startTime} - {currentBlock.endTime}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {scheduleBlocks.map((block, index) => {
          const isCurrent = currentBlock &&
            block.intervalStart === currentBlock.intervalStart &&
            block.intervalEnd === currentBlock.intervalEnd;

          return (
            <div
              key={index}
              className={`flex items-center justify-between p-3 rounded-lg border-2 transition-all ${
                isCurrent
                  ? 'bg-blue-50 border-blue-300'
                  : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={isCurrent ? 'text-blue-600' : 'text-gray-600'}>
                  {getActivityIcon(block.activityType, block.channel)}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    {getActivityBadge(block.activityType, block.channel)}
                    {block.channel && block.activityType !== block.channel && (
                      <span className="text-xs text-gray-500">({block.activityType})</span>
                    )}
                  </div>
                </div>
              </div>
              <div className={`text-sm font-medium ${isCurrent ? 'text-blue-900' : 'text-gray-700'}`}>
                {block.startTime} - {block.endTime}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function AgentScheduleView() {
  const user = useAuthStore((state) => state.user);
  const [weekOffset, setWeekOffset] = useState(0); // 0 = current week, -1 = previous week, 1 = next week

  // Calculate the date range for the current week view
  const { startDate, endDate, displayDates } = useMemo(() => {
    const today = new Date();
    // Calculate the start of the week (7 days from today + offset)
    const start = new Date(today);
    start.setDate(today.getDate() + (weekOffset * 7));
    start.setHours(0, 0, 0, 0);

    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    end.setHours(23, 59, 59, 999);

    // Generate array of 7 dates for display
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      dates.push(date);
    }

    return {
      startDate: start.toISOString(),
      endDate: end.toISOString(),
      displayDates: dates,
    };
  }, [weekOffset]);

  // Fetch shifts for the agent
  const { data: shifts = [], isLoading } = useShifts(undefined, user?.agentId);

  // Filter shifts to only those in the current date range
  const weekShifts = useMemo(() => {
    return shifts.filter((shift: Shift) => {
      const shiftDate = new Date(shift.startTime);
      const start = new Date(startDate);
      const end = new Date(endDate);
      return shiftDate >= start && shiftDate <= end;
    });
  }, [shifts, startDate, endDate]);

  // Group shifts by date
  const shiftsByDate = useMemo(() => {
    const grouped: { [key: string]: Shift[] } = {};
    weekShifts.forEach((shift: Shift) => {
      const dateKey = new Date(shift.startTime).toDateString();
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(shift);
    });
    return grouped;
  }, [weekShifts]);

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const getShiftTypeBadge = (type?: string) => {
    switch (type) {
      case 'WORK':
        return <Badge variant="success">Work</Badge>;
      case 'PTO':
        return <Badge variant="info">PTO</Badge>;
      case 'SICK':
        return <Badge variant="warning">Sick</Badge>;
      case 'TRAINING':
        return <Badge variant="default">Training</Badge>;
      case 'MEETING':
        return <Badge variant="default">Meeting</Badge>;
      case 'UNSCHEDULED':
        return <Badge variant="error">Unscheduled</Badge>;
      default:
        return <Badge variant="default">Shift</Badge>;
    }
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const getWeekLabel = () => {
    if (weekOffset === 0) return 'This Week';
    if (weekOffset === -1) return 'Last Week';
    if (weekOffset === 1) return 'Next Week';
    if (weekOffset < 0) return `${Math.abs(weekOffset)} Weeks Ago`;
    return `${weekOffset} Weeks Ahead`;
  };

  return (
    <Card>
      <div className="mb-4">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-semibold">My Schedule</h3>
            <p className="text-sm text-gray-600">{getWeekLabel()}</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={() => setWeekOffset((prev) => prev - 1)}
              className="px-3"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="secondary"
              onClick={() => setWeekOffset(0)}
              disabled={weekOffset === 0}
              className="px-4"
            >
              Today
            </Button>
            <Button
              variant="secondary"
              onClick={() => setWeekOffset((prev) => prev + 1)}
              className="px-3"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="text-center py-8 text-gray-500">Loading schedule...</div>
        ) : (
          <div className="grid grid-cols-7 gap-2">
            {displayDates.map((date, index) => {
              const dateKey = date.toDateString();
              const dayShifts = shiftsByDate[dateKey] || [];
              const today = isToday(date);

              return (
                <div
                  key={index}
                  className={`border rounded-lg p-3 ${
                    today ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                  }`}
                >
                  <div className="text-center mb-2">
                    <div className={`text-xs font-medium ${today ? 'text-blue-600' : 'text-gray-600'}`}>
                      {date.toLocaleDateString('en-US', { weekday: 'short' })}
                    </div>
                    <div className={`text-lg font-bold ${today ? 'text-blue-900' : 'text-gray-900'}`}>
                      {date.getDate()}
                    </div>
                    <div className="text-xs text-gray-500">
                      {date.toLocaleDateString('en-US', { month: 'short' })}
                    </div>
                  </div>

                  <div className="space-y-1">
                    {dayShifts.length === 0 ? (
                      <div className="text-xs text-gray-400 text-center py-2">No shifts</div>
                    ) : (
                      dayShifts.map((shift: Shift) => (
                        <div
                          key={shift.id}
                          className="bg-white rounded p-2 text-xs border border-gray-100"
                        >
                          <div className="font-medium text-gray-900 mb-1">
                            {formatTime(shift.startTime)} - {formatTime(shift.endTime)}
                          </div>
                          <div className="flex justify-center">
                            {getShiftTypeBadge(shift.shiftType)}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Card>
  );
}

function RecentPunches() {
  const user = useAuthStore((state) => state.user);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'MANAGER';

  // Get last 7 days of punches - memoize to prevent recalculation on every render
  const { startDate, endDate } = useMemo(() => {
    const end = new Date();
    const start = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    return {
      startDate: start.toISOString(),
      endDate: end.toISOString(),
    };
  }, []); // Empty dependency array - only calculate once

  // Use organization punches for admins, agent punches for agents
  const { data: orgPunches = [], isLoading: orgLoading, error: orgError } = usePunchesByOrganization(
    startDate,
    endDate
  );
  const { data: agentPunches = [], isLoading: agentLoading, error: agentError } = usePunchesByAgent(
    user?.agentId || '',
    startDate,
    endDate
  );

  const punches = isAdmin ? orgPunches : agentPunches;
  const isLoading = isAdmin ? orgLoading : agentLoading;
  const error = isAdmin ? orgError : agentError;

  // Debug logging
  console.log('RecentPunches component:', {
    user,
    isAdmin,
    orgPunches: orgPunches.length,
    agentPunches: agentPunches.length,
    punches: punches.length,
    isLoading,
    error,
  });

  // Sort punches from newest to oldest
  const sortedPunches = [...punches].sort((a, b) => {
    return new Date(b.punchTime).getTime() - new Date(a.punchTime).getTime();
  });

  // Pagination calculations
  const totalPages = Math.ceil(sortedPunches.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedPunches = sortedPunches.slice(startIndex, endIndex);

  // Reset to page 1 when page size changes
  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  const getPunchTypeBadge = (type: PunchType) => {
    switch (type) {
      case PunchType.CLOCK_IN:
        return <Badge variant="success">Clock In</Badge>;
      case PunchType.CLOCK_OUT:
        return <Badge variant="error">Clock Out</Badge>;
      case PunchType.BREAK_START:
        return <Badge variant="warning">Break Start</Badge>;
      case PunchType.BREAK_END:
        return <Badge variant="info">Break End</Badge>;
      case PunchType.LUNCH_START:
        return <Badge variant="warning">Lunch Start</Badge>;
      case PunchType.LUNCH_END:
        return <Badge variant="info">Lunch End</Badge>;
      default:
        return <Badge variant="default">{type}</Badge>;
    }
  };

  const formatDateTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return {
      date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      time: date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
    };
  };

  return (
    <Card>
      <div className="mb-4 flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">Recent Punches</h3>
          <p className="text-sm text-gray-600">Last 7 days ({sortedPunches.length} total) - Newest first</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Show:</label>
          <select
            value={pageSize}
            onChange={(e) => handlePageSizeChange(Number(e.target.value))}
            className="px-3 py-1 border border-gray-300 rounded-lg text-sm"
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
        </div>
      </div>

      <Card padding="none">
        <Table>
          <TableHeader>
            <TableRow>
              {isAdmin && <TableHead>Agent</TableHead>}
              <TableHead>Date</TableHead>
              <TableHead>Time</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={isAdmin ? 5 : 4} className="text-center text-gray-500">
                  Loading punches...
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={isAdmin ? 5 : 4} className="text-center text-red-500">
                  Error loading punches: {error.message}
                </TableCell>
              </TableRow>
            ) : sortedPunches.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isAdmin ? 5 : 4} className="text-center text-gray-500">
                  No recent punches found
                </TableCell>
              </TableRow>
            ) : (
              paginatedPunches.map((punch: any) => {
                const { date, time } = formatDateTime(punch.punchTime);
                return (
                  <TableRow key={punch.id}>
                    {isAdmin && (
                      <TableCell>
                        {punch.agent ? `${punch.agent.firstName} ${punch.agent.lastName}` : 'Unknown'}
                      </TableCell>
                    )}
                    <TableCell>{date}</TableCell>
                    <TableCell>{time}</TableCell>
                    <TableCell>{getPunchTypeBadge(punch.punchType)}</TableCell>
                    <TableCell>
                      {punch.status === 'APPROVED' && <Badge variant="success">Approved</Badge>}
                      {punch.status === 'PENDING' && <Badge variant="default">Pending</Badge>}
                      {punch.status === 'EXCEPTION' && <Badge variant="error">Exception</Badge>}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Pagination Controls */}
      {!isLoading && sortedPunches.length > 0 && (
        <div className="mt-4 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            Showing {startIndex + 1} to {Math.min(endIndex, sortedPunches.length)} of {sortedPunches.length} punches
          </div>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 py-1 rounded-lg text-sm ${
                    page === currentPage
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {page}
                </button>
              ))}
            </div>
            <Button
              variant="secondary"
              onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
